import type { TFunction } from 'i18next';
import type { DailyTasksResponse } from '../../types/task';
import type { ExpectedRelanceDto, RelanceListItemDto } from '../../types/customerFollowUp/relance';
import { CreditStatus } from '../../types/customerFollowUp/enums';
import type { TaskGroup } from '../../components/Dialogue/types';
import type { RelanceTaskItem } from '../../types/customerFollowUp/relanceTask';
import { getCategoryLabel, getCategoryRoutePrefix } from '../relance/taskCategoryUtils';
import { TaskCategory } from '../../types/enums';
import { formatCurrency } from '../formatters';

// Only a nearly reached (80 %+) or reached limit is shown, rounded.
const creditLimitPercent = (item: RelanceListItemDto): number | null =>
    item.flags.creditStatus !== CreditStatus.NONE && item.creditLimitReachedPercent !== null
        ? Math.round(item.creditLimitReachedPercent)
        : null;

export function mapRelanceItemToTaskItem(item: RelanceListItemDto, language: string, isForceCompleted = false): RelanceTaskItem {
    return {
        id: item.id,
        customerId: item.customerId,
        title: item.customerName,
        description: '',
        amount: formatCurrency(item.totalDue, language),
        isCompleted: isForceCompleted,
        customerExternalId: item.customerExternalId,
        transactionReference: item.transactionReference,
        totalDue: item.totalDue,
        relanceCount: item.relanceCount,
        daysLate: item.daysLate,
        overdueDocumentCount: item.overdueDocumentCount,
        statementSentOn: item.statementSentOn,
        creditLimitPercent: creditLimitPercent(item),
    };
}

// The id is prefixed so it can never be taken for a relance id: an expected relance does not exist yet.
export function mapExpectedRelanceToTaskItem(dto: ExpectedRelanceDto, language: string): RelanceTaskItem {
    return {
        id: `expected-${dto.customerId}`,
        customerId: dto.customerId,
        title: dto.customerName,
        description: '',
        amount: formatCurrency(dto.projectedTotalDue, language),
        isCompleted: false,
        customerExternalId: dto.customerExternalId,
        transactionReference: null,
        totalDue: dto.projectedTotalDue,
        relanceCount: dto.relanceCount,
        daysLate: null,
        overdueDocumentCount: dto.projectedOverdueDocumentCount,
    };
}

export function mapDailyTasksToGroups(data: DailyTasksResponse, completedTaskIds: string[] = [], t: TFunction, language = 'fr'): TaskGroup<RelanceTaskItem>[] {
    const completedSet = new Set(completedTaskIds);

    return data.sections.map((section, sectionIndex) => {
        const items = section.type === TaskCategory.CUSTOMER_RELANCE ? section.items : [];
        return {
            id: String(sectionIndex),
            label: getCategoryLabel(section.type, t),
            routePrefix: getCategoryRoutePrefix(section.type),
            expanded: true,
            items: items.map(item => mapRelanceItemToTaskItem(item, language, completedSet.has(item.id))),
        };
    });
}
